import { useState, useEffect, useCallback } from 'react';
import { Stack, Center, Loader, Text, Group, Button, Paper, Alert } from '@mantine/core';
import { CheckCircle, AlertTriangle } from 'react-feather';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { cohortCompensationApi } from '../../../../services/api/cohortCompensation';
import { cohortBillcomApi, billcomApi } from '../../../../services/api/billcom';
import type { CoachVendorStatus, BillComPayment } from '../../../../services/api/billcom';
import type {
  CohortWithDetails,
  CohortCompensationResponse,
  CompensationCalculationResponse,
} from '../../../../types';
import { AUDIENCE_TYPE_LABELS } from '../../../../types';
import { CompensationZeroState } from './CompensationZeroState';
import { CompensationHeader } from './CompensationHeader';
import { ConfigureCompensationModal } from './ConfigureCompensationModal';
import { CoachCompensationTable } from './CoachCompensationTable';
import { CoachBreakdownDrawer } from './CoachBreakdownDrawer';
import { BulkConfirmModal } from './BulkConfirmModal';
import { CompensationCallouts } from './CompensationCallouts';
import { PaymentsDueTable } from './PaymentsDueTable';
import { LinkVendorModal } from '../../../admin/compensation-settings/components/LinkVendorModal';
import { ScheduledPaymentsSection } from './ScheduledPaymentsSection';
import { PaymentSummaryCard } from './PaymentSummaryCard';
import { FailedPaymentsAlert } from './FailedPaymentsAlert';

interface CompensationSubTabProps {
  cohort: CohortWithDetails;
  hasCoaches: boolean;
  /**
   * MED-1024: Notify the parent (CoachesTab) whenever the cohort's
   * compensation configuration changes (save or unlock), so the parent's
   * `isCompensationConfigured` gate can re-evaluate without a hard refresh.
   */
  onConfigurationChange?: () => void;
}

export function CompensationSubTab({
  cohort,
  hasCoaches,
  onConfigurationChange,
}: CompensationSubTabProps) {
  const [data, setData] = useState<CohortCompensationResponse | null>(null);
  const [calcData, setCalcData] = useState<CompensationCalculationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [configModalOpened, { open: openConfigModal, close: closeConfigModal }] =
    useDisclosure(false);
  const [bulkConfirmOpened, { open: openBulkConfirm, close: closeBulkConfirm }] =
    useDisclosure(false);
  const [selectedCoachUserId, setSelectedCoachUserId] = useState<string | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  // Bill.com vendor mapping state (AC7 / E13-5)
  const [vendorStatusMap, setVendorStatusMap] = useState<Map<string, CoachVendorStatus>>(new Map());
  const [unlinkedCoachCount, setUnlinkedCoachCount] = useState(0);
  const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] = useDisclosure(false);
  const [linkModalCoach, setLinkModalCoach] = useState<{
    userId: string;
    name: string;
    email: string;
  } | null>(null);

  // Net-10 pending payment counts (E13-6)
  const [net10PendingCount, setNet10PendingCount] = useState(0);
  const [net10OverdueCount, setNet10OverdueCount] = useState(0);
  const [net10TotalCents, setNet10TotalCents] = useState(0);

  // Payment summary refresh key (E13-7)
  const [paymentRefreshKey, setPaymentRefreshKey] = useState(0);
  const [failedPayments, setFailedPayments] = useState<
    Array<{ id: string; coach_name?: string; amount_cents: number }>
  >([]);

  const fetchConfig = useCallback(async () => {
    try {
      const result = await cohortCompensationApi.getConfig(cohort.id);
      setData(result);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load compensation config',
        color: 'red',
      });
    }
  }, [cohort.id]);

  const fetchCalculation = useCallback(async () => {
    try {
      const result = await cohortCompensationApi.getCalculation(cohort.id);
      setCalcData(result);
    } catch {
      // Silently fail — calculation may not exist yet
      setCalcData(null);
    }
  }, [cohort.id]);

  const fetchVendorStatus = useCallback(async () => {
    try {
      const coaches = await cohortBillcomApi.getCoachVendorStatus(cohort.id);
      const map = new Map<string, CoachVendorStatus>();
      // Only assigned coaches count as payment blockers — potential coaches
      // may still be unmapped without blocking scheduling.
      let unmapped = 0;
      for (const c of coaches) {
        map.set(c.user_id, c);
        if (!c.billcom_vendor_id && c.pool_status === 'assigned') unmapped++;
      }
      setVendorStatusMap(map);
      setUnlinkedCoachCount(unmapped);
    } catch {
      // Silently fail — Bill.com may not be configured
    }

    // Fetch failed payments (E13-7 AC12)
    try {
      const payments = await cohortBillcomApi.getPayments(cohort.id, { status: ['failed'] });
      setFailedPayments(payments as BillComPayment[]);
    } catch {
      // Silently fail
    }
  }, [cohort.id]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchConfig(), fetchCalculation(), fetchVendorStatus()]);
    setLoading(false);
  }, [fetchConfig, fetchCalculation, fetchVendorStatus]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSaved = useCallback(() => {
    closeConfigModal();
    fetchAll();
    // MED-1024: refresh the parent's compensation gate so the Send
    // Availability Request button unlocks without a hard refresh (AC1–AC3).
    onConfigurationChange?.();
    notifications.show({
      title: 'Compensation Configured',
      message: 'Compensation configuration has been saved',
      color: 'green',
    });
  }, [closeConfigModal, fetchAll, onConfigurationChange]);

  const handleUnlock = useCallback(() => {
    modals.openConfirmModal({
      title: 'Unlock Compensation',
      children: (
        <Text size="sm">
          This will unlock the compensation configuration, allowing changes. The rate snapshot will
          be cleared. Continue?
        </Text>
      ),
      labels: { confirm: 'Unlock', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          await cohortCompensationApi.unlock(cohort.id);
          fetchAll();
          // MED-1024 AC4: re-evaluate the parent gate from authoritative
          // server state after a config change, without a hard refresh.
          onConfigurationChange?.();
          notifications.show({
            title: 'Compensation Unlocked',
            message: 'Configuration can now be edited',
            color: 'green',
          });
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to unlock compensation',
            color: 'red',
          });
        }
      },
    });
  }, [cohort.id, fetchAll, onConfigurationChange]);

  const handleViewCoach = useCallback(
    (coachUserId: string) => {
      setSelectedCoachUserId(coachUserId);
      openDrawer();
    },
    [openDrawer],
  );

  const handleDrawerUpdated = useCallback(() => {
    fetchCalculation();
  }, [fetchCalculation]);

  const handleBulkConfirmed = useCallback(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLinkVendor = useCallback(
    (coachUserId: string, coachName: string, coachEmail: string) => {
      setLinkModalCoach({ userId: coachUserId, name: coachName, email: coachEmail });
      openLinkModal();
    },
    [openLinkModal],
  );

  const handleVendorLinked = useCallback(() => {
    fetchVendorStatus();
  }, [fetchVendorStatus]);

  // MED-1178: unlink a coach's Bill.com vendor (with a warning when scheduled
  // payments already reference it). Re-mapping is handled by the existing link
  // modal (link POST overwrites), so no separate re-map path is needed.
  const handleUnlinkVendor = useCallback(
    (coachUserId: string, coachName: string, vendorName: string, hasPayments: boolean) => {
      modals.openConfirmModal({
        title: 'Unlink Bill.com vendor',
        children: (
          <Stack gap="sm">
            <Text size="sm">
              Unlink <strong>{coachName}</strong> from the Bill.com vendor{' '}
              <strong>{vendorName || 'this vendor'}</strong>? You can link a different vendor
              afterward.
            </Text>
            {hasPayments && (
              <Alert color="warningAmber" variant="light" icon={<AlertTriangle size={16} />}>
                This coach already has scheduled payments created against the current vendor.
                Unlinking won&rsquo;t change those payments &mdash; review them in Scheduled
                Payments and void or reschedule as needed.
              </Alert>
            )}
          </Stack>
        ),
        labels: { confirm: 'Unlink vendor', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          try {
            await billcomApi.unlinkVendor(coachUserId);
            notifications.show({
              title: 'Vendor unlinked',
              message: `${coachName} is no longer linked to a Bill.com vendor.`,
              color: 'green',
            });
            fetchVendorStatus();
          } catch {
            notifications.show({
              title: 'Error',
              message: 'Failed to unlink vendor',
              color: 'red',
            });
          }
        },
      });
    },
    [fetchVendorStatus],
  );

  const handleNet10CountChange = useCallback(
    (pending: number, overdue: number, totalCents: number) => {
      setNet10PendingCount(pending);
      setNet10OverdueCount(overdue);
      setNet10TotalCents(totalCents);
    },
    [],
  );

  // MED-1383: one refresh for every payment view. A submit, a retry or a finished
  // scheduling job bumps the shared key, so the summary card, both payment tables and
  // the Sent to Bill.com list refetch together. fetchVendorStatus reloads failed rows.
  const handlePaymentsChanged = useCallback(() => {
    setPaymentRefreshKey((k) => k + 1);
    fetchVendorStatus();
  }, [fetchVendorStatus]);

  const handleExportCsv = useCallback(async () => {
    try {
      await cohortCompensationApi.exportCsv(cohort.id);
    } catch {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export compensation CSV',
        color: 'red',
      });
    }
  }, [cohort.id]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  const config = data?.config ?? null;
  const isConfigured = config !== null;
  const audienceTypeLabel = config ? AUDIENCE_TYPE_LABELS[config.audience_type] : undefined;

  // Zero state: not configured yet, or configured but no coaches
  if (!isConfigured || (isConfigured && !hasCoaches)) {
    return (
      <>
        <CompensationZeroState
          hasCoaches={hasCoaches}
          isConfigured={isConfigured}
          audienceTypeLabel={audienceTypeLabel}
          onConfigure={openConfigModal}
        />
        <ConfigureCompensationModal
          opened={configModalOpened}
          onClose={closeConfigModal}
          cohortId={cohort.id}
          config={config}
          resolvedRates={data?.resolved_rates ?? null}
          cohortModalities={data?.cohort_modalities ?? []}
          sessionDuration={data?.session_duration_minutes ?? null}
          configuredOneOnOneSessionTypes={
            data?.configured_one_on_one_session_types ?? []
          }
          configuredOneOnOneDurations={data?.configured_one_on_one_durations ?? []}
          hasInPersonEvents={data?.has_in_person_events ?? false}
          calculationInputs={data?.calculation_inputs ?? null}
          onSaved={handleSaved}
        />
      </>
    );
  }

  const allCoaches = calcData?.coaches ?? [];
  const pendingCoaches = calcData?.pending_coaches ?? [];
  const hasCalculation = calcData?.status === 'calculated' && (allCoaches.length > 0 || pendingCoaches.length > 0);
  const hasCalculatedCoaches = allCoaches.length > 0;
  const totalCoachCount = allCoaches.length + pendingCoaches.length;

  // Configured with coaches — show header + coach compensation table
  return (
    <Stack gap="lg">
      {/* Callout banners (AC7) */}
      <CompensationCallouts
        paymentsPendingCount={net10PendingCount}
        paymentsOverdueCount={net10OverdueCount}
        unlinkedCoachCount={unlinkedCoachCount}
      />

      <CompensationHeader
        audienceTypeLabel={audienceTypeLabel!}
        sessionDuration={data?.session_duration_minutes ?? null}
        isConfirmed={config.is_confirmed}
        totalEstimatedCents={calcData?.total_estimated_cents}
        assignedTotalEstimatedCents={calcData?.assigned_total_estimated_cents}
        potentialTotalEstimatedCents={calcData?.potential_total_estimated_cents}
        systemCalculatedCount={calcData?.system_calculated_count}
        confirmedCount={calcData?.confirmed_count}
        pendingCount={calcData?.pending_count}
        coachCount={totalCoachCount}
        paymentsDueCount={net10PendingCount}
        paymentsOverdueCount={net10OverdueCount}
        paymentsDueCents={net10TotalCents}
        hasCalculation={hasCalculation}
        onConfigure={openConfigModal}
        onUnlock={handleUnlock}
        onExportCsv={handleExportCsv}
      />

      {hasCalculation ? (
        <>
          <CoachCompensationTable
            coaches={allCoaches}
            pendingCoaches={pendingCoaches}
            onViewCoach={handleViewCoach}
            vendorStatus={vendorStatusMap}
            onLinkVendor={handleLinkVendor}
            onUnlinkVendor={handleUnlinkVendor}
          />
          {/* MED-675: Confirm All footer row, aligned bottom-right beneath the coach list */}
          {(calcData?.system_calculated_count ?? 0) > 0 && (
            <Paper
              p="md"
              radius="md"
              style={{ border: '1px solid #E8E6E1', background: 'white' }}
            >
              <Group justify="flex-end">
                <Button
                  leftSection={<CheckCircle size={16} />}
                  onClick={openBulkConfirm}
                >
                  Confirm All ({calcData?.system_calculated_count})
                </Button>
              </Group>
            </Paper>
          )}
        </>
      ) : (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          No compensation records yet. Ensure coaches are assigned to groups, then refresh.
        </Text>
      )}

      {/* Payment Summary (E13-7 AC11) */}
      {hasCalculatedCoaches && (
        <PaymentSummaryCard cohortId={cohort.id} refreshKey={paymentRefreshKey} />
      )}

      {/* Failed Payment Alerts (E13-7 AC12) */}
      {hasCalculatedCoaches && failedPayments.length > 0 && (
        <FailedPaymentsAlert
          cohortId={cohort.id}
          failedPayments={failedPayments as BillComPayment[]}
          onRetrySuccess={handlePaymentsChanged}
        />
      )}

      {/* Scheduled Payments (Flat Rate) — E13-5 */}
      {hasCalculatedCoaches && (
        <ScheduledPaymentsSection
          cohortId={cohort.id}
          canSchedule={!!config?.is_confirmed && (calcData?.confirmed_count ?? 0) > 0}
          hasUnmappedCoaches={unlinkedCoachCount > 0}
          refreshKey={paymentRefreshKey}
          onPaymentsChanged={handlePaymentsChanged}
        />
      )}

      {/* Payments Due (Net 10) — E13-6 */}
      {hasCalculatedCoaches && (
        <PaymentsDueTable
          cohortId={cohort.id}
          onCountChange={handleNet10CountChange}
          refreshKey={paymentRefreshKey}
          onPaymentsChanged={handlePaymentsChanged}
        />
      )}

      <ConfigureCompensationModal
        opened={configModalOpened}
        onClose={closeConfigModal}
        cohortId={cohort.id}
        config={config}
        resolvedRates={data?.resolved_rates ?? null}
        cohortModalities={data?.cohort_modalities ?? []}
        sessionDuration={data?.session_duration_minutes ?? null}
        configuredOneOnOneSessionTypes={
          data?.configured_one_on_one_session_types ?? []
        }
        configuredOneOnOneDurations={data?.configured_one_on_one_durations ?? []}
        hasInPersonEvents={data?.has_in_person_events ?? false}
        calculationInputs={data?.calculation_inputs ?? null}
        onSaved={handleSaved}
      />

      <CoachBreakdownDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        cohortId={cohort.id}
        coachUserId={selectedCoachUserId}
        onUpdated={handleDrawerUpdated}
      />

      {hasCalculatedCoaches && (
        <BulkConfirmModal
          opened={bulkConfirmOpened}
          onClose={closeBulkConfirm}
          cohortId={cohort.id}
          coaches={allCoaches}
          onConfirmed={handleBulkConfirmed}
        />
      )}

      {/* Link Vendor Modal (E13-5) */}
      {linkModalCoach && (
        <LinkVendorModal
          opened={linkModalOpened}
          onClose={closeLinkModal}
          coachUserId={linkModalCoach.userId}
          coachName={linkModalCoach.name}
          coachEmail={linkModalCoach.email}
          onLinked={handleVendorLinked}
        />
      )}
    </Stack>
  );
}
